import React, { useEffect, useRef, useState } from "react";
import { useInRouterContext, useNavigate } from "react-router-dom";
import { showSuccessToast } from "../../constants/ToastService";
import { qualityApi } from "./qualityApi";
import { CASE_TYPES, Modal, SEVERITY_LEVELS, TEST_RESULTS, hasPerm } from "./qualityUi";
import useQualityMeta, { subModulesOf } from "./useQualityMeta";

/**
 * Add or edit a test case in the global library.
 *
 * Laid out in the QA team's sheet order: Module, Sub Module, Screen, Test Case
 * Scenario, Test Case Description, Pre-Condition, Test Steps, Test Type,
 * Severity, Test Data, Expected Result, Actual Result, Result. The same columns
 * come in through Import (TestCaseImportModal).
 *
 * One form for every entry point: the library itself, a sprint ticket's Test
 * Cases tab (`context.ticketId`), and a production bug's preventive action
 * (`context.bugId` + `context.actionId`), which arrives pre-filled from the bug.
 */

// Test steps are typed as one numbered list: "1. Open billing", Enter gives
// "2. ", and so on. A step's own expected result can follow an arrow
// ("Return 2 items → credit note created"); the sheet has none, so it is optional.
const STEP_NUM = /^\s*(?:step\s*)?\d+\s*[.):-]\s*/i;
const STEP_ARROW = /\s*(?:→|->)\s*/;

const stepsToText = (steps) => (steps || [])
    .filter((s) => (s.action || "").trim() || (s.expected || "").trim())
    .map((s, i) => `${i + 1}. ${s.action || ""}${s.expected ? ` → ${s.expected}` : ""}`)
    .join("\n");

export const textToSteps = (text) => (text || "").split("\n")
    .map((line) => line.replace(STEP_NUM, "").trim())
    .filter(Boolean)
    .map((line) => {
        const [action, ...rest] = line.split(STEP_ARROW);
        return { action: action.trim(), expected: rest.join(" → ").trim() };
    });

// "1. a\n\nfoo\n7. b" -> "1. a\n2. foo\n3. b" (blank lines dropped).
const renumber = (text) => text.split("\n")
    .filter((line) => line.replace(STEP_NUM, "").trim())
    .map((line, i) => `${i + 1}. ${line.replace(STEP_NUM, "").trim()}`)
    .join("\n");

const fromCase = (tc) => ({
    module: tc.module || "",
    sub_module: tc.sub_module || "",
    screen: tc.screen || "",
    // A case saved before these columns existed only had a title.
    scenario: tc.scenario || (tc.id ? "" : tc.title || ""),
    description: tc.description || (tc.id ? tc.title || "" : ""),
    preconditions: tc.preconditions || "",
    stepsText: stepsToText(tc.steps),
    case_type: tc.case_type || "Positive",
    severity: tc.severity || "",
    test_data: tc.test_data || "",
    expected_result: tc.expected_result || "",
    actual_result: tc.actual_result || "",
    result: tc.result || "",
    priority: tc.priority || "Medium",
    regression: tc.critical ? "critical" : tc.in_regression === false ? "none" : "regression",
    status: tc.status || "Active",
    applies_to: tc.applies_to || "all",
    setting_name: tc.setting_name || "",
    owner: tc.owner || "",
    tags: tc.tags || "",
});

// Same rules the backend enforces; checked here too so the user sees every
// problem at once instead of one round trip per mistake.
const validate = (f) => {
    const errors = [];
    const steps = textToSteps(f.stepsText);
    if (!f.scenario.trim() && !f.description.trim()) errors.push("Add the test case scenario or description.");
    if (!f.sub_module) errors.push("Choose the sub module.");
    if (!f.preconditions.trim()) errors.push("Add the pre-condition (the setup needed before step 1).");
    if (!steps.length) errors.push("Add at least one test step.");
    steps.forEach((s, i) => { if (!s.action.trim()) errors.push(`Step ${i + 1} has an expected result but no action.`); });
    if (!f.expected_result.trim() && !steps.some((s) => s.expected.trim())) errors.push("Add the expected result.");
    if (f.case_type.trim().length > 32) errors.push("Test type must be 32 characters or fewer.");
    if (f.applies_to === "setting" && !f.setting_name.trim()) errors.push("Name the setting this case depends on.");
    return { errors, steps };
};

// Opens a linked ticket in its sprint (same deep link the fix ticket uses).
const ViewTicket = ({ ticketRef, onGo }) => {
    const navigate = useNavigate();
    return (
        <button type="button" className="qa-chip__view" title={`Open ${ticketRef.code} in its sprint`}
            onClick={() => { onGo && onGo(); navigate(`/sprints?sprint=${ticketRef.sprint_id}&ticket=${ticketRef.id}`); }}>
            View
        </button>
    );
};

export default function TestCaseForm({ testCase, prefill, context = {}, onClose, onSaved, onDeleted, onOpenOther, onUnlinked }) {
    const meta = useQualityMeta();
    const editing = !!(testCase && testCase.id);
    const [f, setF] = useState(() => fromCase({ ...(prefill || {}), ...(testCase || {}) }));
    const [errors, setErrors] = useState([]);
    const [similar, setSimilar] = useState([]);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [inUse, setInUse] = useState("");
    const canDelete = editing && !!onDeleted && hasPerm("Manage Test Cases");
    const stepsRef = useRef(null);
    const caretAt = useRef(null);

    const subs = subModulesOf(meta, f.module);
    const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

    // Fill defaults once reference data arrives.
    useEffect(() => {
        if (!meta.modules.length) return;
        setF((prev) => {
            const module = prev.module || meta.modules[0].name;
            const list = subModulesOf(meta, module);
            return {
                ...prev,
                module,
                sub_module: list.includes(prev.sub_module) ? prev.sub_module : list[0] || "",
                owner: prev.owner || meta.members[0] || "",
            };
        });
    }, [meta]);

    // Duplicate check while typing the scenario / description, debounced.
    const lookFor = `${f.scenario} ${f.description}`.trim();
    useEffect(() => {
        const t = setTimeout(() => {
            if (lookFor.length < 8) { setSimilar([]); return; }
            qualityApi.similarTestCases(lookFor.slice(0, 300), testCase ? testCase.code : "")
                .then((res) => setSimilar(res.data || []))
                .catch(() => setSimilar([]));
        }, 300);
        return () => clearTimeout(t);
    }, [lookFor, testCase]);

    // Keep the caret where the key handler put it, and grow the box with its text.
    useEffect(() => {
        const el = stepsRef.current;
        if (!el) return;
        if (caretAt.current !== null) {
            el.setSelectionRange(caretAt.current, caretAt.current);
            caretAt.current = null;
        }
        el.style.height = "auto";
        el.style.height = `${Math.max(el.scrollHeight + 2, 180)}px`;
    }, [f.stepsText]);

    const setStepsText = (text, caret = null) => { caretAt.current = caret; set({ stepsText: text }); };

    const onStepsKeyDown = (e) => {
        const el = e.target;
        const { value, selectionStart: start, selectionEnd: end } = el;
        const lineStart = value.lastIndexOf("\n", start - 1) + 1;
        if (e.key === "Enter" && !e.shiftKey) {
            // New numbered line; the lines below shift down by one.
            e.preventDefault();
            const before = value.slice(0, start);
            const after = value.slice(end);
            const n = before.split("\n").length + 1;
            const head = `${before}\n${n}. `;
            // The rest of the current line moves to the new step; lines below get +1.
            const [frag, ...below] = after.split("\n");
            const rest = below.map((line) => {
                const m = line.match(/^(\s*)(\d+)([.):-]\s*)/);
                return m ? `${m[1]}${Number(m[2]) + 1}${m[3]}${line.slice(m[0].length)}` : line;
            });
            setStepsText([head + frag.replace(/^\s+/, ""), ...rest].join("\n"), head.length);
        } else if (e.key === "Backspace" && start === end && start > 0) {
            // Backspace on an empty "3. " removes the whole line and renumbers.
            const lineEnd = value.indexOf("\n", start) === -1 ? value.length : value.indexOf("\n", start);
            const line = value.slice(lineStart, lineEnd);
            if (STEP_NUM.test(line) && !line.replace(STEP_NUM, "").trim() && start === lineEnd) {
                e.preventDefault();
                const kept = (value.slice(0, Math.max(lineStart - 1, 0)) + value.slice(lineEnd)).replace(/^\n/, "");
                const text = lineStart === 0 ? "" : renumber(kept);
                const upto = lineStart === 0 ? 0 : renumber(value.slice(0, lineStart - 1)).length;
                setStepsText(text, upto);
            }
        }
    };

    const save = async (e) => {
        e.preventDefault();
        const { errors: errs, steps } = validate(f);
        if (errs.length) { setErrors(errs); return; }
        setSaving(true);
        const payload = {
            module: f.module, sub_module: f.sub_module, screen: f.screen.trim(),
            scenario: f.scenario.trim(), description: f.description.trim(),
            preconditions: f.preconditions.trim(),
            steps: steps.map((s) => ({ action: s.action.trim(), expected: s.expected.trim() })),
            case_type: f.case_type.trim() || "Positive", severity: f.severity, test_data: f.test_data.trim(),
            expected_result: f.expected_result.trim(), actual_result: f.actual_result.trim(), result: f.result,
            priority: f.priority, in_regression: f.regression !== "none", critical: f.regression === "critical",
            status: f.status, applies_to: f.applies_to, setting_name: f.applies_to === "setting" ? f.setting_name.trim() : "",
            owner: f.owner, tags: f.tags.trim(),
        };
        if (!editing) {
            if (context.ticketId) payload.ticket_id = context.ticketId;
            else if (context.ticketCode) payload.ticket_code = context.ticketCode;
            if (context.bugId) { payload.bug_id = context.bugId; payload.action_id = context.actionId; }
        }
        try {
            const res = editing
                ? await qualityApi.updateTestCase(testCase.id, payload)
                : await qualityApi.createTestCase(payload);
            showSuccessToast(res.message || "Test case saved.");
            onSaved && onSaved(res.data);
        } catch (err) {
            setErrors(err.payload?.errors?.length ? err.payload.errors : [err.message || "Could not save the test case."]);
        } finally {
            setSaving(false);
        }
    };

    const doDelete = async () => {
        setSaving(true);
        try {
            const res = await qualityApi.deleteTestCase(testCase.id);
            showSuccessToast(res.message || `${testCase.code} deleted.`);
            onDeleted(res.data);
        } catch (err) {
            setConfirmDelete(false);
            // A case that is release history can't be deleted; offer to retire it.
            if (err.payload?.code === "in_use") setInUse(err.message);
            else setErrors([err.message || "Could not delete the test case."]);
        } finally {
            setSaving(false);
        }
    };

    const deprecate = async () => {
        setSaving(true);
        try {
            const res = await qualityApi.updateTestCase(testCase.id, { status: "Deprecated" });
            showSuccessToast(`${testCase.code} deprecated. It stays in history and is left out of future runs.`);
            onSaved && onSaved(res.data);
        } catch (err) {
            setErrors(err.payload?.errors?.length ? err.payload.errors : [err.message || "Could not deprecate the test case."]);
        } finally {
            setSaving(false);
        }
    };

    // Ticket links with ids can be unlinked here. Older servers send codes only;
    // then just the ticket this form was opened from (context) is unlinkable.
    const [refs, setRefs] = useState(() => {
        if (!editing) return [];
        const withIds = testCase.linked_ticket_refs || [];
        const codes = new Set(withIds.map((r) => r.code));
        const rest = (testCase.linked_tickets || []).filter((c) => !codes.has(c))
            .map((c) => ({ id: c === context.ticketCode ? context.ticketId : null, code: c }));
        return [...withIds, ...rest];
    });
    const [unlinking, setUnlinking] = useState(null);
    const canUnlink = editing && hasPerm("Manage Test Cases");
    const inRouter = useInRouterContext();
    const unlinkTicket = async (ref) => {
        if (!window.confirm(`Unlink ${testCase.code} from ${ref.code}? The case stays in the library.`)) return;
        setUnlinking(ref.code);
        try {
            await qualityApi.unlinkTicketTestCase(ref.id, testCase.code);
            setRefs((prev) => prev.filter((r) => r.code !== ref.code));
            showSuccessToast(`${testCase.code} unlinked from ${ref.code}.`);
            onUnlinked && onUnlinked(ref);
        } catch (err) {
            setErrors([err.message || "Could not unlink the test case."]);
        } finally {
            setUnlinking(null);
        }
    };
    const linked = editing ? refs.map((r) => r.code) : (context.ticketCode ? [context.ticketCode] : []);
    const linkedBugs = [
        ...((testCase && testCase.linked_bugs) || []),
        ...(context.bugCode && !editing ? [context.bugCode] : []),
    ];

    const text = (id, key, label, placeholder, { req, area, full } = {}) => (
        <label className={`qa-fl${full ? " qa-full" : ""}`}>
            <span className={req ? "qa-req" : ""}>{label}</span>
            {area ? (
                <textarea id={id} className="qa-input" value={f[key]} placeholder={placeholder} onChange={(e) => set({ [key]: e.target.value })} />
            ) : (
                <input id={id} className="qa-input" value={f[key]} placeholder={placeholder} onChange={(e) => set({ [key]: e.target.value })} />
            )}
        </label>
    );

    return (
        <Modal
            wide
            title={editing ? <>Edit <span className="qa-mono">{testCase.code}</span></> : "New test case"}
            onClose={onClose}
            footer={confirmDelete ? (
                <>
                    <span className="qa-hint" style={{ color: "var(--qa-red)" }}>
                        Delete {testCase.code}? It is removed from the library and from any tickets it is linked to.
                    </span>
                    <span className="qa-sp" />
                    <button type="button" className="qa-btn" onClick={() => setConfirmDelete(false)} disabled={saving}>Keep it</button>
                    <button type="button" className="qa-btn qa-btn--danger" onClick={doDelete} disabled={saving}>
                        {saving ? "Deleting…" : `Delete ${testCase.code}`}
                    </button>
                </>
            ) : (
                <>
                    {canDelete && (
                        <button type="button" className="qa-btn qa-btn--danger" onClick={() => { setInUse(""); setConfirmDelete(true); }}>
                            Delete
                        </button>
                    )}
                    <span className="qa-hint">
                        {f.status === "Active"
                            ? `Active cases join the next regression run for ${f.module || "this module"}.`
                            : "Not Active, so it stays out of regression runs."}
                    </span>
                    <span className="qa-sp" />
                    <button type="button" className="qa-btn" onClick={onClose}>Cancel</button>
                    <button type="submit" form="qa-tc-form" className="qa-btn qa-btn--primary" disabled={saving}>
                        {saving ? "Saving…" : editing ? "Save changes" : "Add to library"}
                    </button>
                </>
            )}
        >
            <form id="qa-tc-form" onSubmit={save} noValidate>
                {inUse && (
                    <div className="qa-banner qa-banner--warn">
                        <span className="qa-banner__tx"><b>Can't delete.</b> {inUse}</span>
                        {f.status !== "Deprecated" && (
                            <button type="button" className="qa-btn qa-btn--sm" onClick={deprecate} disabled={saving}>
                                Mark as Deprecated
                            </button>
                        )}
                    </div>
                )}
                {errors.length > 0 && (
                    <div className="qa-banner qa-banner--bad">
                        <span className="qa-banner__tx">
                            <b>Fix these before saving:</b>
                            {errors.map((x) => <div key={x}>{x}</div>)}
                        </span>
                    </div>
                )}
                {context.bugCode && !editing && (
                    <div className="qa-banner qa-banner--info">
                        <span className="qa-banner__tx">
                            Preventive action for <b>{context.bugCode}</b>. The bug scenario is copied into the
                            pre-condition and the type is set to {f.case_type}. Write steps that reproduce what slipped through.
                        </span>
                    </div>
                )}

                {/* ── Where ── */}
                <div className="qa-fsec">
                    <div className="qa-sec"><h4>Where</h4></div>
                    <div className="qa-form">
                        <label className="qa-fl">
                            <span className="qa-req">Module</span>
                            <select
                                id="qa-tc-module" className="qa-input" value={f.module}
                                onChange={(e) => set({ module: e.target.value, sub_module: subModulesOf(meta, e.target.value)[0] || "" })}
                            >
                                {meta.modules.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
                            </select>
                        </label>
                        <label className="qa-fl">
                            <span className="qa-req">Sub module</span>
                            <select id="qa-tc-sub" className="qa-input" value={f.sub_module} onChange={(e) => set({ sub_module: e.target.value })}>
                                {subs.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </label>
                        {text("qa-tc-screen", "screen", "Screen", "e.g. Billing tab, Purchase Inward screen", { full: true })}
                    </div>
                </div>

                {/* ── The case ── */}
                <div className="qa-fsec">
                    <div className="qa-sec"><h4>Test case</h4></div>
                    <div className="qa-form">
                        <label className="qa-fl qa-full">
                            Test case scenario
                            <input
                                id="qa-tc-scenario" className="qa-input" value={f.scenario}
                                onChange={(e) => set({ scenario: e.target.value })}
                                placeholder="e.g. Pending amount after a partial sales return"
                            />
                        </label>
                        <label className="qa-fl qa-full">
                            <span className="qa-req">Test case description</span>
                            <textarea
                                id="qa-tc-description" className="qa-input" value={f.description}
                                onChange={(e) => set({ description: e.target.value })}
                                placeholder="What exactly is being verified"
                            />
                            {similar.length > 0 && (
                                <div className="qa-dups">
                                    <b>Similar cases already in the library.</b> Could you extend one of these instead of adding a near-copy?
                                    {similar.map((s) => (
                                        <button type="button" key={s.code} onClick={() => onOpenOther && onOpenOther(s)}>
                                            {s.code} · {s.title} ({s.sub_module})
                                        </button>
                                    ))}
                                </div>
                            )}
                        </label>
                        {text("qa-tc-pre", "preconditions", "Pre-condition", "Setup needed before step 1, e.g. Bill-cancel window = 24h, logged in as admin", { req: true, area: true, full: true })}
                    </div>
                </div>

                {/* ── Test steps ── */}
                <div className="qa-fsec">
                    <div className="qa-sec">
                        <h4 className="qa-req">Test steps <span className="qa-hint">
                            One step per line. Press Enter for the next number. Optional: "action → expected" for a step's own result.
                        </span></h4>
                    </div>
                    <textarea
                        id="qa-tc-steps" ref={stepsRef} className="qa-input qa-steps-list" value={f.stepsText}
                        aria-label="Test steps" spellCheck
                        placeholder={"1. Open Pharmacy → Billing for the patient\n2. Return 2 items from the bill\n3. Check the Pending amount"}
                        onFocus={(e) => { if (!e.target.value) setStepsText("1. ", 3); }}
                        onBlur={() => { const t = renumber(f.stepsText); if (t !== f.stepsText) set({ stepsText: t }); }}
                        onKeyDown={onStepsKeyDown}
                        onChange={(e) => setStepsText(e.target.value)}
                        onPaste={(e) => {
                            // Pasted lines (numbered or not) become numbered steps.
                            const pasted = e.clipboardData.getData("text");
                            if (!pasted.includes("\n")) return;
                            e.preventDefault();
                            const el = e.target;
                            const merged = el.value.slice(0, el.selectionStart) + pasted.replace(/\r/g, "") + el.value.slice(el.selectionEnd);
                            const t = renumber(merged);
                            setStepsText(t, t.length);
                        }}
                    />
                    {textToSteps(f.stepsText).length > 0 && (
                        <div className="qa-hint" style={{ marginTop: 4 }}>
                            {textToSteps(f.stepsText).length} step{textToSteps(f.stepsText).length === 1 ? "" : "s"}
                        </div>
                    )}
                </div>

                {/* ── Type, severity, data, results ── */}
                <div className="qa-fsec">
                    <div className="qa-sec"><h4>Type &amp; results</h4></div>
                    <div className="qa-form">
                        <label className="qa-fl">
                            Test type
                            <input
                                id="qa-tc-type" className="qa-input" list="qa-tc-types" value={f.case_type} maxLength={32}
                                onChange={(e) => set({ case_type: e.target.value })} placeholder="e.g. Positive, Negative, Functional"
                            />
                            <datalist id="qa-tc-types">{CASE_TYPES.map((t) => <option key={t} value={t} />)}</datalist>
                        </label>
                        <label className="qa-fl">
                            Severity
                            <select id="qa-tc-severity" className="qa-input" value={f.severity} onChange={(e) => set({ severity: e.target.value })}>
                                <option value="">—</option>
                                {SEVERITY_LEVELS.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </label>
                        {text("qa-tc-data", "test_data", "Test data", "e.g. UHID 10045 · item PARA-500 · amount ₹1,250", { full: true })}
                        {text("qa-tc-expected", "expected_result", "Expected result", "What should happen", { req: true, area: true })}
                        {text("qa-tc-actual", "actual_result", "Actual result", "What happened when it was last run by hand", { area: true })}
                        <label className="qa-fl">
                            Result
                            <select id="qa-tc-result" className="qa-input" value={f.result} onChange={(e) => set({ result: e.target.value })}>
                                <option value="">—</option>
                                {TEST_RESULTS.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                            <span className="qa-typehint">Results from a release's regression run replace this.</span>
                        </label>
                        <label className="qa-fl">
                            Priority
                            <select id="qa-tc-priority" className="qa-input" value={f.priority} onChange={(e) => set({ priority: e.target.value })}>
                                <option value="High">High · always run</option>
                                <option value="Medium">Medium</option>
                                <option value="Low">Low · run when time allows</option>
                            </select>
                        </label>
                    </div>
                </div>

                {/* ── Scope & ownership ── */}
                <div className="qa-fsec">
                    <div className="qa-sec"><h4>Scope &amp; ownership</h4></div>
                    <div className="qa-form">
                        <label className="qa-fl">
                            Regression
                            <select id="qa-tc-reg" className="qa-input" value={f.regression} onChange={(e) => set({ regression: e.target.value })}>
                                <option value="regression">Include in regression</option>
                                <option value="critical">Regression + critical (every run)</option>
                                <option value="none">Not in regression</option>
                            </select>
                        </label>
                        <label className="qa-fl">
                            Status
                            <select id="qa-tc-status" className="qa-input" value={f.status} onChange={(e) => set({ status: e.target.value })}>
                                <option value="Active">Active · used in runs</option>
                                <option value="Draft">Draft · not used yet</option>
                                <option value="Deprecated">Deprecated · kept for history</option>
                            </select>
                        </label>
                        <label className="qa-fl">
                            Applies to
                            <select id="qa-tc-scope" className="qa-input" value={f.applies_to} onChange={(e) => set({ applies_to: e.target.value })}>
                                <option value="all">All hospitals</option>
                                <option value="setting">Only hospitals with a setting on</option>
                            </select>
                        </label>
                        {f.applies_to === "setting" ? (
                            <label className="qa-fl">
                                <span className="qa-req">Setting</span>
                                <input
                                    id="qa-tc-setting" className="qa-input" value={f.setting_name}
                                    onChange={(e) => set({ setting_name: e.target.value })} placeholder="e.g. Bill cancel window enabled"
                                />
                            </label>
                        ) : <div />}
                        <label className="qa-fl">
                            Owner
                            <select id="qa-tc-owner" className="qa-input" value={f.owner} onChange={(e) => set({ owner: e.target.value })}>
                                {meta.members.map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </label>
                        {text("qa-tc-tags", "tags", "Tags", "e.g. billing-critical, month-end")}
                        <div className="qa-fl qa-full">
                            Linked
                            <div className="qa-meta">
                                {editing ? refs.map((r) => (
                                    <span key={r.code} className="qa-chip qa-chip--accent">
                                        {r.code}
                                        {inRouter && r.id && r.sprint_id && r.id !== context.ticketId && (
                                            <ViewTicket ticketRef={r} onGo={onClose} />
                                        )}
                                        {canUnlink && r.id && (
                                            <button type="button" className="qa-chip__x" disabled={unlinking === r.code}
                                                title={`Unlink from ${r.code}`} aria-label={`Unlink from ${r.code}`}
                                                onClick={() => unlinkTicket(r)}>×</button>
                                        )}
                                    </span>
                                )) : linked.map((t) => <span key={t} className="qa-chip qa-chip--accent">{t}</span>)}
                                {linkedBugs.map((b) => <span key={b} className="qa-chip qa-chip--red">{b}</span>)}
                                {!linked.length && !linkedBugs.length && (
                                    <span className="qa-faint">Not linked yet. Link it from a ticket's Test Cases tab.</span>
                                )}
                            </div>
                            {canUnlink && refs.some((r) => !r.id) && (
                                <span className="qa-hint">To unlink a ticket without ×, use Unlink on that ticket's Test Cases tab.</span>
                            )}
                        </div>
                    </div>
                </div>
            </form>
        </Modal>
    );
}
